import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  Chip,
  Alert,
  CircularProgress,
  Tooltip,
  Stack,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { conditionPhotoSettingsService } from '../services/api';
import { ConditionPhotoType } from '../types';

interface ConditionPhotoSettingsDialogProps {
  open: boolean;
  onClose: () => void;
}

const ConditionPhotoSettingsDialog: React.FC<ConditionPhotoSettingsDialogProps> = ({ open, onClose }) => {
  const [types, setTypes] = useState<ConditionPhotoType[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    name_en: '',
    description: '',
    icon: '📷',
    color: '#607d8b',
    sort_order: 0,
  });

  useEffect(() => {
    if (open) {
      loadTypes();
    }
  }, [open]);

  const loadTypes = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await conditionPhotoSettingsService.getSettings();
      setTypes(response.data.data.types || []);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur lors du chargement des types');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    try {
      setError('');
      await conditionPhotoSettingsService.createType(formData);
      await loadTypes();
      setShowAddForm(false);
      resetForm();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur lors de la création du type');
    }
  };

  const handleUpdate = async (id: number) => {
    try {
      setError('');
      const type = types.find((t) => t.id === id);
      if (type) {
        await conditionPhotoSettingsService.updateType(id, {
          code: type.code,
          name: type.name,
          name_en: type.name_en,
          description: type.description,
          icon: type.icon,
          color: type.color,
          sort_order: type.sort_order,
        });
        await loadTypes();
        setEditingId(null);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur lors de la mise à jour du type');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Êtes-vous sûr de vouloir désactiver ce type de photo ?')) {
      return;
    }

    try {
      setError('');
      await conditionPhotoSettingsService.deleteType(id);
      await loadTypes();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur lors de la suppression du type');
    }
  };

  const handleEdit = (type: ConditionPhotoType) => {
    setEditingId(type.id);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    loadTypes();
  };

  const handleFieldChange = (id: number, field: keyof ConditionPhotoType, value: any) => {
    setTypes(types.map((t) => (t.id === id ? { ...t, [field]: value } : t)));
  };

  const resetForm = () => {
    setFormData({
      code: '',
      name: '',
      name_en: '',
      description: '',
      icon: '📷',
      color: '#607d8b',
      sort_order: 0,
    });
  };

  const handleClose = () => {
    setEditingId(null);
    setShowAddForm(false);
    resetForm();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          background: 'linear-gradient(135deg, #1f2933 0%, #374151 100%)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6" component="span">
            📷 Types de Photos (État des Lieux)
          </Typography>
        </Box>
        <IconButton onClick={handleClose} sx={{ color: 'white' }} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {showAddForm && (
              <Paper sx={{ p: 2, mb: 2, backgroundColor: '#f5f5f5' }}>
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                  Nouveau Type de Photo
                </Typography>
                <Stack spacing={2}>
                  <TextField
                    label="Code"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                    size="small"
                    required
                    helperText="Identifiant unique (ex: exterior_front)"
                  />
                  <TextField
                    label="Nom (FR)"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    size="small"
                    required
                    fullWidth
                  />
                  <TextField
                    label="Nom (EN)"
                    value={formData.name_en}
                    onChange={(e) => setFormData({ ...formData, name_en: e.target.value })}
                    size="small"
                    fullWidth
                  />
                  <TextField
                    label="Description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    size="small"
                    multiline
                    rows={2}
                    fullWidth
                  />
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                      label="Icône (emoji)"
                      value={formData.icon}
                      onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                      size="small"
                      sx={{ width: 120 }}
                    />
                    <TextField
                      label="Couleur (hex)"
                      value={formData.color}
                      onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                      size="small"
                      type="color"
                      sx={{ width: 120 }}
                    />
                    <TextField
                      label="Ordre"
                      value={formData.sort_order}
                      onChange={(e) =>
                        setFormData({ ...formData, sort_order: parseInt(e.target.value, 10) || 0 })
                      }
                      size="small"
                      type="number"
                      sx={{ width: 120 }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<CancelIcon />}
                      onClick={() => {
                        setShowAddForm(false);
                        resetForm();
                      }}
                    >
                      Annuler
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<SaveIcon />}
                      onClick={handleAdd}
                      disabled={!formData.code || !formData.name}
                    >
                      Ajouter
                    </Button>
                  </Box>
                </Stack>
              </Paper>
            )}

            {!showAddForm && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setShowAddForm(true)}
                sx={{ mb: 2 }}
              >
                Ajouter un type
              </Button>
            )}

            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Code</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Icône</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Nom</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Couleur</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Ordre</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {types.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          Aucun type de photo configuré
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    types.map((type) => (
                      <TableRow key={type.id} hover>
                        <TableCell>
                          {editingId === type.id ? (
                            <TextField
                              value={type.code}
                              onChange={(e) => handleFieldChange(type.id, 'code', e.target.value)}
                              size="small"
                              fullWidth
                            />
                          ) : (
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {type.code}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {editingId === type.id ? (
                            <TextField
                              value={type.icon || ''}
                              onChange={(e) => handleFieldChange(type.id, 'icon', e.target.value)}
                              size="small"
                              sx={{ width: 60 }}
                            />
                          ) : (
                            <Typography variant="h6">{type.icon || '📷'}</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {editingId === type.id ? (
                            <TextField
                              value={type.name}
                              onChange={(e) => handleFieldChange(type.id, 'name', e.target.value)}
                              size="small"
                              fullWidth
                            />
                          ) : (
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {type.name}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {editingId === type.id ? (
                            <TextField
                              value={type.description || ''}
                              onChange={(e) => handleFieldChange(type.id, 'description', e.target.value)}
                              size="small"
                              fullWidth
                              multiline
                            />
                          ) : (
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                              {type.description || '-'}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {editingId === type.id ? (
                            <TextField
                              value={type.color || '#607d8b'}
                              onChange={(e) => handleFieldChange(type.id, 'color', e.target.value)}
                              size="small"
                              type="color"
                              sx={{ width: 80 }}
                            />
                          ) : (
                            <Chip
                              label={type.color || '#607d8b'}
                              size="small"
                              sx={{
                                backgroundColor: type.color || '#607d8b',
                                color: 'white',
                                fontWeight: 500,
                                fontSize: '0.75rem',
                              }}
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          {editingId === type.id ? (
                            <TextField
                              value={type.sort_order ?? 0}
                              onChange={(e) =>
                                handleFieldChange(type.id, 'sort_order', parseInt(e.target.value, 10) || 0)
                              }
                              size="small"
                              type="number"
                              sx={{ width: 70 }}
                            />
                          ) : (
                            <Typography variant="body2">{type.sort_order}</Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {editingId === type.id ? (
                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                              <Tooltip title="Enregistrer">
                                <IconButton size="small" color="primary" onClick={() => handleUpdate(type.id)}>
                                  <SaveIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Annuler">
                                <IconButton size="small" onClick={handleCancelEdit}>
                                  <CancelIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          ) : (
                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                              <Tooltip title="Modifier">
                                <IconButton size="small" onClick={() => handleEdit(type)}>
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Désactiver">
                                <IconButton size="small" color="error" onClick={() => handleDelete(type.id)}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} variant="outlined">
          Fermer
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConditionPhotoSettingsDialog;

